import moment from 'moment';
import _ from 'lodash';
import { apiService } from "./apiService";
import { CaseTypes, ICase } from "../types/case/case";
import { IAbductor, CompanionTypes } from "../types/case/abductor";
import { IChild } from "../types/case/children";
import { momentToMiliseconds, formatCapitalized } from '../utils/utils';
import { CasesService } from './cases';
import { IAgency } from '../types/agency';
import { ICaseRow } from '../types/case/caseUpload';

export interface ISaveImageReq {
  caseId: string,
  id: string,
  providers: any,
  imageId: number,
  image: string,
  type: 'Child' | 'Abductor'
}
export interface ISaveMiscImageReq {
  caseId: string,
  imageId: string,
  image: string,
}
export const CasesUploadService = {
  uploadCases: (cases: ICaseRow[]) =>
    apiService.post('/api/cases/upload', { cases }),
  getCase(caseRow: ICaseRow, agencies: IAgency[]) {
    const { caseId, agencyCode, caseType, public: publicStatus, status, poster, circumstances,
      country, state, city, address, lastSeen, missingDate, openDate, miscellaneous } = caseRow;
    const agencyData = agencies.find(agency => agency.code === agencyCode) as IAgency;
    const caseData: ICase = {
      agencyCode,
      contactInformation: agencyData.contact,
      caseId,
      caseType: this.getCaseType(caseType),
      poster,
      public: publicStatus,
      status,
      circumstances,
      country,
      state,
      city,
      address,
      children: [],
      abductors: [],
      miscellaneous: miscellaneous ? miscellaneous.split(',') : [],
      lastSeen,
      openDate: moment(openDate, 'DD-MM-YYYY'),
      missingDate: moment(missingDate, 'DD-MM-YYYY'),
      sendMatches: ''
    };
    return caseData;
  },
  getCaseType(caseType: string) {
    return Object.keys(CaseTypes).find(ctype => ctype === caseType) ? caseType as CaseTypes : CaseTypes.EndangeredMiss;
  },
  getPerson(caseRow: ICaseRow) {
    const { fullName, birthDate, age, sex, eyeColor, hairColor, height, heightUnit, weight, weightUnit,
      portraitURL, alternateURL, progressedURL, personType, companionType } = caseRow;
    const personData: IChild | IAbductor = {
      childId: caseRow.personType === 'child' ? caseRow.personId : '',
      abductorId: caseRow.personType === 'abductor' ? caseRow.personId : '',
      fullName,
      birthDate: moment(birthDate, 'DD-MM-YYYY'),
      age,
      sex: sex.toString() === 'male' ? 'Male' : sex.toString() === 'female' ? 'Female' : 'Unknown',
      eyeColor,
      hairColor,
      height: height.toString(),
      heightUnit,
      weight: weight.toString(),
      weightUnit,
      images: [portraitURL, alternateURL, progressedURL],
      companionType: personType === 'child' ? undefined : this.getCompanionType(companionType)
    };
    return personData;
  },
  getCompanionType(caseType: string) {
    return Object.keys(CompanionTypes).find(ctype => ctype === caseType) ? caseType as CompanionTypes : CompanionTypes.Abductor;
  },
  formatCaseDB(newCase) {
    const caseDB = { ...newCase };
    const openDate = momentToMiliseconds(caseDB.openDate);
    const missingDate = momentToMiliseconds(caseDB.missingDate);
    if (!openDate || !missingDate) {
      throw { error: 'Error parsing the case row' };
    }
    caseDB.openDate = openDate;
    caseDB.missingDate = missingDate;
    caseDB.public = typeof caseDB.public === 'string' ? caseDB.public.toLowerCase() === 'true' ? true : false : caseDB.public;
    caseDB.caseId = `${caseDB.agencyCode}-${caseDB.caseId}`;
    caseDB.children = this.formatPersonDB(caseDB, 'children');
    caseDB.abductors = this.formatPersonDB(caseDB, 'abductors');
    caseDB.miscellaneous = caseDB.miscellaneous.reduce((miscellaneous, path) => {
      miscellaneous[path] = true;
      return miscellaneous;
    }, {});
    CasesService.removeUnusedProperties(caseDB);
    return caseDB;
  },
  formatPersonDB(caseDB, type: 'children' | 'abductors') {
    const personId = type === 'children' ? 'childId' : 'abductorId';
    return caseDB[type] ? caseDB[type].map((personFE) => {
      const personDB = { ...personFE };
      const birthDate = momentToMiliseconds(personDB.birthDate);
      if (!birthDate) {
        throw { error: 'Error parsing the case row' };
      }
      personDB[personId] = (personDB[personId] as string).toString();
      personDB.birthDate = birthDate;
      delete personDB.age;
      personDB.fullName = formatCapitalized(personDB.fullName);
      CasesService.removeUnusedProperties(personDB);
      CasesService.removeUnitProperties(personDB);
      if (personDB.images.length > 0) {
        personDB.images = {
          ...personDB.images[0] ? { portrait: personDB.images[0] } : {},
          ...personDB.images[1] ? { alternate: personDB.images[1] } : {},
          ...personDB.images[2] ? { progressed: personDB.images[2] } : {},
        };
      }
      return personDB;
    }) : []
  },
  formatCasesList(casesToFormat: ICase[], casesError: string[] = []) {
    const casesList = casesToFormat.reduce((cases, caseData) => {
      try {
        const formattedCase = this.formatCaseDB(caseData);
        cases.push(formattedCase);
        return cases;
      } catch {
        casesError.push(`${caseData.agencyCode}-${caseData.caseId}`);
        return cases;
      }
    }, [] as any[])
    return { casesList, casesError };
  },
  getCases(casesRows: ICaseRow[], agencies: IAgency[]) {
    const cases = casesRows.reduce((casesList, caseRow) => {
      const index = casesList.findIndex(caseSaved => caseSaved.caseId === caseRow.caseId && caseSaved.agencyCode === caseRow.agencyCode);
      const personData = this.getPerson(caseRow);
      if (index >= 0) {
        casesList[index] = this.addPersonData(caseRow, casesList[index], personData);
        return casesList;
      } else {
        const newCase = this.getNewCase(caseRow, personData, agencies);
        casesList.push(newCase);
        return casesList;
      }
    }, [] as ICase[]);
    return this.formatCasesList(cases);
  },
  addPersonData(caseRow: ICaseRow, caseData: ICase, personData: IChild | IAbductor) {
    const caseInfo = _.cloneDeep(caseData);
    if (caseRow.personType === 'child') {
      caseInfo.children.push(personData as IChild);
    } else {
      caseInfo.abductors.push(personData as IAbductor);
    }
    return caseInfo;
  },
  getNewCase(caseRow: ICaseRow, personData: IChild | IAbductor, agencies: IAgency[]) {
    const newCase = this.getCase(caseRow, agencies);
    if (caseRow.personType === 'child') {
      newCase.children.push(personData as IChild);
    } else {
      newCase.abductors.push(personData as IAbductor);
    }
    return newCase;
  },
  getExcelTemplateURL: () => `https://caseview-children-assets.s3.us-east-2.amazonaws.com/Templates/cases_template.csv`
}